export type ScratchBrushSet = {
  stamps: HTMLCanvasElement[]
  diameter: number
  drawSize: number
}

const SCRATCH_BRUSH_VARIANT_COUNT = 6
const SCRATCH_BRUSH_PADDING = 8

function pseudoRandom(seed: number, variantIndex: number): number {
  const value = Math.sin(seed * 127.1 + variantIndex * 311.7) * 43758.5453
  return value - Math.floor(value)
}

function buildIrregularBrushStamp(diameter: number, variantIndex: number): HTMLCanvasElement {
  const size = Math.ceil(diameter + SCRATCH_BRUSH_PADDING * 2)
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size

  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('Não foi possível criar o pincel de raspagem.')
  }

  const centerX = size / 2
  const centerY = size / 2
  const baseRadius = diameter / 2

  context.fillStyle = '#000000'

  const lobeCount = 11
  context.beginPath()

  for (let index = 0; index <= lobeCount; index += 1) {
    const angle = (index / lobeCount) * Math.PI * 2
    const radiusJitter = 0.58 + pseudoRandom(index, variantIndex) * 0.42
    const pointX = centerX + Math.cos(angle) * baseRadius * radiusJitter
    const pointY = centerY + Math.sin(angle) * baseRadius * radiusJitter

    if (index === 0) {
      context.moveTo(pointX, pointY)
      continue
    }

    const previousAngle = ((index - 0.5) / lobeCount) * Math.PI * 2
    const controlRadius = baseRadius * (0.62 + pseudoRandom(index + 40, variantIndex) * 0.38)
    const controlX = centerX + Math.cos(previousAngle) * controlRadius
    const controlY = centerY + Math.sin(previousAngle) * controlRadius
    context.quadraticCurveTo(controlX, controlY, pointX, pointY)
  }

  context.closePath()
  context.fill()

  const bumpCount = 6
  for (let bumpIndex = 0; bumpIndex < bumpCount; bumpIndex += 1) {
    const angle = pseudoRandom(bumpIndex + 90, variantIndex) * Math.PI * 2
    const distance = pseudoRandom(bumpIndex + 120, variantIndex) * baseRadius * 0.62
    const bumpRadius = baseRadius * (0.1 + pseudoRandom(bumpIndex + 150, variantIndex) * 0.22)

    context.beginPath()
    context.arc(
      centerX + Math.cos(angle) * distance,
      centerY + Math.sin(angle) * distance,
      bumpRadius,
      0,
      Math.PI * 2
    )
    context.fill()
  }

  const scratchCount = 4
  for (let scratchIndex = 0; scratchIndex < scratchCount; scratchIndex += 1) {
    const angle = pseudoRandom(scratchIndex + 200, variantIndex) * Math.PI * 2
    const scratchLength = baseRadius * (0.35 + pseudoRandom(scratchIndex + 230, variantIndex) * 0.45)
    const scratchWidth = 1.2 + pseudoRandom(scratchIndex + 260, variantIndex) * 2.2
    const startDistance = pseudoRandom(scratchIndex + 290, variantIndex) * baseRadius * 0.35

    const startX = centerX + Math.cos(angle) * startDistance
    const startY = centerY + Math.sin(angle) * startDistance
    const endX = startX + Math.cos(angle) * scratchLength
    const endY = startY + Math.sin(angle) * scratchLength

    context.lineCap = 'round'
    context.lineWidth = scratchWidth
    context.strokeStyle = '#000000'
    context.beginPath()
    context.moveTo(startX, startY)
    context.lineTo(endX, endY)
    context.stroke()
  }

  return canvas
}

export function createScratchBrushSet(diameter: number): ScratchBrushSet {
  const stamps = Array.from({ length: SCRATCH_BRUSH_VARIANT_COUNT }, (_, variantIndex) =>
    buildIrregularBrushStamp(diameter, variantIndex)
  )

  return {
    stamps,
    diameter,
    drawSize: Math.ceil(diameter + SCRATCH_BRUSH_PADDING * 2),
  }
}

export function pickScratchBrushStamp(
  brushSet: ScratchBrushSet,
  seed: number
): { stamp: HTMLCanvasElement; rotation: number } {
  const stampIndex = Math.abs(Math.floor(seed)) % brushSet.stamps.length
  const stamp = brushSet.stamps[stampIndex]
  if (!stamp) {
    throw new Error('Pincel de raspagem indisponível.')
  }

  const rotation = pseudoRandom(seed * 0.17, stampIndex) * Math.PI * 2

  return { stamp, rotation }
}
